/**
 * /hustlechat               — inbox (list of conversations)
 * /hustlechat/c/:convId     — thread view (read + send messages)
 * /hustlechat/new/:memberId — start a new chat with a specific member
 *
 * All encryption is performed locally via hcCrypto.js — the server
 * never sees cleartext.
 */
import { useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import {
  ArrowLeft, Send, MessageSquare, Loader2, Lock, ShieldAlert, Flag,
  Plus, Search,
} from "lucide-react";
import {
  ensureKeyPair, getMyPubJwk, encryptFor, decryptFrom, safetyScreen,
} from "../lib/hcCrypto";
import { useChatSocket } from "../lib/useChatSocket";

const API = process.env.REACT_APP_BACKEND_URL || (typeof window !== "undefined" ? window.location.origin : "");
const api = axios.create({ baseURL: API });
const TKN = () => localStorage.getItem("hu_session_token");

const css = `
  .hc{min-height:100vh;background:#fffef5;color:#0f172a;font-family:Inter,sans-serif}
  .hc-top{display:flex;justify-content:space-between;align-items:center;
          padding:14px 18px;background:#0f172a;color:#fbbf24;position:sticky;top:0;z-index:30}
  .hc-back{color:#fbbf24;text-decoration:none;display:inline-flex;align-items:center;gap:6px;font-size:13px}
  .hc-main{max-width:680px;margin:0 auto;padding:18px;padding-bottom:120px}
  .hc-h1{font-family:Georgia,serif;font-size:28px;font-weight:800;margin:0 0 4px;letter-spacing:-0.025em}
  .hc-sub{color:#475569;font-size:13px;margin:0 0 18px;line-height:1.5;display:inline-flex;align-items:center;gap:6px}
  .hc-row{display:flex;align-items:center;gap:14px;background:white;
          border:1px solid #e8e2d4;border-radius:14px;padding:14px;margin-bottom:10px;cursor:pointer;width:100%;text-align:left;font:inherit;color:inherit}
  .hc-row:hover{transform:translateY(-1px);box-shadow:0 8px 18px rgba(15,23,42,.05)}
  .hc-avatar{width:46px;height:46px;border-radius:50%;background:#fbbf24;color:#0f172a;
             display:inline-flex;align-items:center;justify-content:center;font-weight:800;font-size:18px;flex-shrink:0}
  .hc-name{font-weight:800;font-size:15px}
  .hc-meta{font-size:12px;color:#64748b}
  .hc-search{display:flex;align-items:center;gap:8px;background:white;border:1px solid #e8e2d4;border-radius:10px;padding:10px 12px;margin-bottom:14px}
  .hc-search input{border:none;outline:none;flex:1;font-size:14px;font-family:inherit;background:transparent}
  .hc-msg{max-width:75%;padding:10px 14px;border-radius:18px;margin-bottom:6px;font-size:14px;line-height:1.45;word-wrap:break-word}
  .hc-msg.in{background:white;border:1px solid #e8e2d4;border-bottom-left-radius:4px}
  .hc-msg.out{background:#0f172a;color:#fbbf24;align-self:flex-end;border-bottom-right-radius:4px;margin-left:auto}
  .hc-meta-row{display:flex;align-items:center;gap:6px;font-size:10px;color:#94a3b8;margin-bottom:8px}
  .hc-meta-row.out{justify-content:flex-end}
  .hc-input-bar{position:fixed;bottom:0;left:0;right:0;background:white;border-top:1px solid #e8e2d4;padding:12px 14px;display:flex;align-items:center;gap:10px;z-index:25}
  .hc-input-bar input{flex:1;padding:12px 16px;border:1px solid #e8e2d4;border-radius:999px;font-size:14px;outline:none;font-family:inherit}
  .hc-send-btn{background:#fbbf24;color:#0f172a;border:none;width:42px;height:42px;border-radius:50%;cursor:pointer;display:inline-flex;align-items:center;justify-content:center;flex-shrink:0}
  .hc-warn{background:#fee2e2;color:#991b1b;padding:10px 14px;border-radius:8px;font-size:13px;margin:8px 0;display:flex;align-items:center;gap:8px}
  .hc-fab{position:fixed;bottom:24px;right:24px;background:#0f172a;color:#fbbf24;border:none;width:54px;height:54px;border-radius:50%;cursor:pointer;display:inline-flex;align-items:center;justify-content:center;box-shadow:0 12px 28px rgba(15,23,42,.25);z-index:24}
  .hc-empty{text-align:center;padding:60px 20px;color:#64748b}
`;

// ───────────────────────────────────────────────────────────────────
// /hustlechat — Inbox
// ───────────────────────────────────────────────────────────────────
export function HustleChatInboxPage() {
  const navigate = useNavigate();
  const [convs, setConvs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");

  useEffect(() => {
    const t = TKN();
    if (!t) { navigate("/hustleup/join"); return; }
    // make sure my keypair exists + my public key is uploaded
    (async () => {
      try {
        await ensureKeyPair();
        const pub = await getMyPubJwk();
        await api.post(`/api/hustlechat/keys?token=${t}`, { pub_jwk: pub });
        const r = await api.get(`/api/hustlechat/conversations?token=${t}`);
        setConvs(r.data || []); setLoading(false);
      } catch (e) {
        setErr(e?.response?.data?.detail || e.message); setLoading(false);
      }
    })();
  }, [navigate]);

  return (
    <div className="hc" data-testid="hc-inbox-root">
      <header className="hc-top">
        <a href="/hustleup" className="hc-back">
          <ArrowLeft size={14} /> HustleUp
        </a>
        <span style={{ fontWeight: 700, color: "white" }}>HustleChat</span>
      </header>
      <main className="hc-main">
        <h1 className="hc-h1">Your messages</h1>
        <p className="hc-sub">
          <Lock size={12} /> End-to-end encrypted · text-only · safety screen on
        </p>

        {err && <div className="hc-warn"><ShieldAlert size={14} /> {err}</div>}

        {loading ? (
          <div className="hc-empty"><Loader2 className="spin" /> Loading…</div>
        ) : convs.length === 0 ? (
          <div className="hc-empty" data-testid="hc-inbox-empty">
            <MessageSquare size={32} color="#cbd5e1" />
            <p>No conversations yet. Tap the + button to start one.</p>
          </div>
        ) : (
          convs.map(c => (
            <button key={c.id || c._id} className="hc-row"
                    onClick={() => navigate(`/hustlechat/c/${c.id || c._id}`)}
                    data-testid={`hc-conv-${c.id || c._id}`}>
              <div className="hc-avatar">
                {(c.other?.first_name || "?")[0]?.toUpperCase()}
              </div>
              <div style={{ flex: 1 }}>
                <div className="hc-name">{c.other?.first_name} {c.other?.last_name || ""}</div>
                <div className="hc-meta">
                  {c.last_at ? new Date(c.last_at).toLocaleString("en-AU", {
                    weekday: "short", hour: "numeric", minute: "2-digit",
                  }) : "Start chatting"}
                </div>
              </div>
            </button>
          ))
        )}
      </main>
      <button className="hc-fab" data-testid="hc-fab"
              onClick={() => navigate("/hustlechat/new")}>
        <Plus size={22} />
      </button>
      <style>{css + ".spin{animation:spin 1s linear infinite}@keyframes spin{to{transform:rotate(360deg)}}"}</style>
    </div>
  );
}

// ───────────────────────────────────────────────────────────────────
// /hustlechat/new — pick a member to start chatting with
// ───────────────────────────────────────────────────────────────────
export function HustleChatNewPage() {
  const navigate = useNavigate();
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);

  useEffect(() => {
    const t = TKN();
    if (!t) { navigate("/hustleup/join"); return; }
    if (q.trim().length < 2) { setResults([]); return; }
    const timer = setTimeout(async () => {
      setSearching(true);
      try {
        const r = await api.get(`/api/hustlechat/members/search`,
          { params: { q, token: t } });
        setResults(r.data || []);
      } catch { /* ignore */ }
      setSearching(false);
    }, 280);
    return () => clearTimeout(timer);
  }, [q, navigate]);

  const startChat = async (memberId) => {
    const t = TKN();
    try {
      const r = await api.post(
        `/api/hustlechat/conversations?token=${t}`,
        { other_member_id: memberId },
      );
      navigate(`/hustlechat/c/${r.data.conv_id}`);
    } catch (e) {
      alert(e?.response?.data?.detail || e.message);
    }
  };

  return (
    <div className="hc" data-testid="hc-new-root">
      <header className="hc-top">
        <a href="/hustlechat" className="hc-back">
          <ArrowLeft size={14} /> Inbox
        </a>
        <span style={{ fontWeight: 700, color: "white" }}>New chat</span>
      </header>
      <main className="hc-main">
        <h1 className="hc-h1">Find someone to chat with</h1>
        <p className="hc-sub">
          <Lock size={12} /> Both of you need to have HustleChat enabled.
        </p>
        <div className="hc-search">
          <Search size={14} color="#64748b" />
          <input value={q} onChange={e => setQ(e.target.value)}
                 placeholder="Search by name or email…"
                 data-testid="hc-new-search" />
          {searching && <Loader2 size={14} className="spin" />}
        </div>
        {results.length === 0 && q.length >= 2 && !searching && (
          <div className="hc-empty">No matches.</div>
        )}
        {results.map(r => (
          <button key={r.member_id} className="hc-row"
                  disabled={!r.has_chat}
                  style={{ opacity: r.has_chat ? 1 : 0.55 }}
                  onClick={() => startChat(r.member_id)}
                  data-testid={`hc-pick-${r.member_id}`}>
            <div className="hc-avatar">
              {(r.first_name || "?")[0]?.toUpperCase()}
            </div>
            <div style={{ flex: 1 }}>
              <div className="hc-name">{r.first_name} {r.last_name || ""}</div>
              <div className="hc-meta">
                {r.state || "—"}{r.has_chat ? "" : " · chat not enabled"}
              </div>
            </div>
          </button>
        ))}
      </main>
      <style>{css + ".spin{animation:spin 1s linear infinite}@keyframes spin{to{transform:rotate(360deg)}}"}</style>
    </div>
  );
}

// ───────────────────────────────────────────────────────────────────
// /hustlechat/c/:convId — Thread
// ───────────────────────────────────────────────────────────────────
export function HustleChatThreadPage() {
  const { convId } = useParams();
  const navigate = useNavigate();
  const [conv, setConv] = useState(null);
  const [messages, setMessages] = useState([]); // {id, sender_id, body, ts}
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const [warn, setWarn] = useState("");
  const [myId, setMyId] = useState("");
  const [otherTyping, setOtherTyping] = useState(false);
  const [otherOnline, setOtherOnline] = useState(false);
  const bottomRef = useRef(null);
  const typingTimerRef  = useRef(null);
  const lastTypingSentRef = useRef(0);

  // ── WebSocket bridge ────────────────────────────────────────────
  const sock = useChatSocket({
    enabled:  !!convId,
    convIds:  [convId],
    onNewMessage: async (d) => {
      if (d.conv_id !== convId || d.sender_id === myId) return;
      // Pull just the new message via REST + decrypt
      try {
        const r = await api.get(`/api/hustlechat/messages`,
          { params: { conv_id: convId, token: TKN(), limit: 5 } });
        const fresh = (r.data || []).filter(m => m.id !== undefined || m._id !== undefined);
        const last  = fresh[fresh.length - 1];
        if (!last) return;
        const body = await decryptFrom(last.sender_pub_jwk,
                                        last.ciphertext, last.iv);
        setMessages(prev => {
          if (prev.find(p => p.id === (last.id || last._id))) return prev;
          return [...prev, {
            id: last.id || last._id, sender_id: last.sender_id,
            body, ts: last.created_at,
          }];
        });
        setTimeout(() => bottomRef.current?.scrollIntoView({ behavior: "smooth" }), 30);
      } catch { /* fall back to next poll */ }
    },
    onTyping: (d) => {
      if (d.conv_id !== convId || d.member_id === myId) return;
      setOtherTyping(!!d.on);
      // Auto-clear after 4s if no follow-up
      clearTimeout(typingTimerRef.current);
      if (d.on) {
        typingTimerRef.current = setTimeout(() => setOtherTyping(false), 4000);
      }
    },
    onPresence: (d) => {
      if (d.who && conv?.other?.member_id === d.who) setOtherOnline(!!d.online);
    },
  });

  // Pull conversation metadata + initial messages
  useEffect(() => {
    const t = TKN();
    if (!t) { navigate("/hustleup/join"); return; }
    (async () => {
      try {
        await ensureKeyPair();
        const meRes = await api.get(`/api/hustleup/members/me?token=${t}`);
        setMyId(meRes.data.member_id || meRes.data._id);

        // Resolve "other" via conversations endpoint
        const allConvs = await api.get(`/api/hustlechat/conversations?token=${t}`);
        const c = (allConvs.data || []).find(x => (x.id || x._id) === convId);
        if (!c) { setWarn("Conversation not found."); return; }
        setConv(c);

        await loadMessages(c);
      } catch (e) {
        setWarn(e?.response?.data?.detail || e.message);
      }
    })();
  }, [convId, navigate]);

  const loadMessages = async (c) => {
    const t = TKN();
    const r = await api.get(`/api/hustlechat/messages`,
      { params: { conv_id: c.id || c._id, token: t } });
    const decrypted = [];
    for (const m of r.data || []) {
      // The sender_pub_jwk is whichever side encrypted; we use it + our priv
      // to decrypt (ECDH is symmetric).
      let body = "";
      try {
        body = await decryptFrom(m.sender_pub_jwk, m.ciphertext, m.iv);
      } catch { body = "[unable to decrypt]"; }
      decrypted.push({
        id: m.id || m._id, sender_id: m.sender_id,
        body, ts: m.created_at,
      });
    }
    setMessages(decrypted);
    setTimeout(() => bottomRef.current?.scrollIntoView({ behavior: "smooth" }), 50);
  };

  const send = async () => {
    if (!text.trim() || !conv) return;
    const t = TKN();
    setWarn("");
    // 1. Client-side safety screen
    const flag = safetyScreen(text);
    if (flag) {
      setWarn(`Message blocked — category: ${flag}. This kind of content isn't allowed on HustleChat.`);
      // Log block (no cleartext sent)
      try {
        await api.post(`/api/hustlechat/safety/block?token=${t}`, {
          conv_id: conv.id || conv._id, category: flag,
          hint: text.slice(0, 60),
        });
      } catch { /* ignore */ }
      return;
    }
    setBusy(true);
    try {
      // 2. Encrypt
      const myPub = await getMyPubJwk();
      const { ciphertext, iv } = await encryptFor(conv.other.pub_jwk, text);
      // 3. Send ciphertext only
      const r = await api.post(`/api/hustlechat/messages?token=${t}`, {
        conv_id: conv.id || conv._id,
        ciphertext, iv, sender_pub_jwk: myPub,
      });
      setMessages(m => [...m, {
        id: r.data.message_id, sender_id: myId, body: text,
        ts: r.data.created_at,
      }]);
      setText("");
      setTimeout(() => bottomRef.current?.scrollIntoView({ behavior: "smooth" }), 30);
    } catch (e) {
      setWarn(e?.response?.data?.detail || e.message);
    }
    setBusy(false);
  };

  const report = async (m) => {
    if (!conv) return;
    const reason = window.prompt("Why are you reporting this message?",
      "Inappropriate content");
    if (!reason) return;
    try {
      await api.post(`/api/hustlechat/safety/report?token=${TKN()}`, {
        conv_id: conv.id || conv._id,
        message_id: m.id,
        cleartext: m.body,
        reason,
      });
      alert("Reported to HustleUp safety team — thank you.");
    } catch (e) {
      alert(e?.response?.data?.detail || e.message);
    }
  };

  return (
    <div className="hc" data-testid="hc-thread-root">
      <header className="hc-top">
        <a href="/hustlechat" className="hc-back">
          <ArrowLeft size={14} /> Inbox
        </a>
        <span style={{ fontWeight: 700, color: "white",
                        display: "inline-flex", flexDirection: "column",
                        alignItems: "center", lineHeight: 1.1 }}>
          {conv?.other?.first_name || "…"}
          <span style={{ fontSize: 9, color: otherTyping ? "#fbbf24"
                          : otherOnline ? "#86efac" : "#64748b",
                          fontWeight: 600, marginTop: 1,
                          letterSpacing: 0.5, textTransform: "lowercase" }}
                data-testid="hc-presence-indicator">
            {otherTyping ? "typing…"
              : otherOnline ? "online"
              : sock.connected ? "offline" : "syncing"}
          </span>
        </span>
      </header>
      <main className="hc-main" style={{ display: "flex", flexDirection: "column" }}>
        <p className="hc-sub" style={{ marginBottom: 14 }}>
          <Lock size={12} /> E2EE · only you and {conv?.other?.first_name || "they"} can read these
        </p>

        {warn && <div className="hc-warn"><ShieldAlert size={14} /> {warn}</div>}

        {messages.length === 0 && (
          <div className="hc-empty">
            Say hi — your first message will be encrypted and never seen by us.
          </div>
        )}
        {messages.map(m => {
          const out = m.sender_id === myId;
          return (
            <div key={m.id} style={{ display: "flex", flexDirection: "column",
                                      alignSelf: out ? "flex-end" : "flex-start" }}
                 data-testid={`hc-msg-${m.id}`}>
              <div className={`hc-msg ${out ? "out" : "in"}`}>{m.body}</div>
              <div className={`hc-meta-row ${out ? "out" : ""}`}>
                {m.ts ? new Date(m.ts).toLocaleTimeString("en-AU",
                  { hour: "numeric", minute: "2-digit" }) : ""}
                {!out && (
                  <button onClick={() => report(m)}
                          style={{ background: "transparent", border: "none",
                                    color: "#94a3b8", cursor: "pointer",
                                    padding: 0, marginLeft: 4 }}
                          data-testid={`hc-report-${m.id}`}>
                    <Flag size={10} /> Report
                  </button>
                )}
              </div>
            </div>
          );
        })}
        <div ref={bottomRef} />
      </main>

      <div className="hc-input-bar">
        <input value={text}
               onChange={e => {
                 setText(e.target.value);
                 // Throttle: only send typing=true once every 3s
                 const now = Date.now();
                 if (e.target.value && now - lastTypingSentRef.current > 3000) {
                   sock.sendTyping(convId, true);
                   lastTypingSentRef.current = now;
                 }
                 if (!e.target.value) {
                   sock.sendTyping(convId, false);
                   lastTypingSentRef.current = 0;
                 }
               }}
               placeholder="Type a message…"
               onKeyDown={e => { if (e.key === "Enter") {
                 sock.sendTyping(convId, false);
                 send();
               } }}
               data-testid="hc-thread-input" />
        <button className="hc-send-btn" onClick={send} disabled={busy}
                data-testid="hc-thread-send">
          {busy ? <Loader2 size={18} className="spin" /> : <Send size={18} />}
        </button>
      </div>
      <style>{css + ".spin{animation:spin 1s linear infinite}@keyframes spin{to{transform:rotate(360deg)}}"}</style>
    </div>
  );
}
